import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
  type FilterOptionsState
} from '@mui/material';
import React from 'react';
import { useQueryClient } from 'react-query';
import * as THREE from 'three';
import { i18n } from 'i18n';
import {
  BackfillRecipeDto,
  BackfillRecipeForm,
  CreatePourItem,
  PourPresetDto,
  PourPresetGroupDto,
  useAllProjectRecipesQuery,
  useCreateRecipeMutation,
  useGetPourPresetGroupsQuery
} from 'providers/api';
import useApi from 'providers/api/useApi';
import { usePage } from 'providers/page';
import { useScenario } from 'modules/project/providers/scenario';
import {
  meshVolume,
  LEGACY_BEACH_ANGLE_DEGREES
} from 'modules/project/StopeFillPage/Three/pour';
import useStopeModel from 'modules/project/StopeFillPage/Three/useStopeModel';
import { ModelState, Model } from '../hooks/useModelState';
import { PCStope } from '../MineModel/Stopes';
import { buildMediaUrl } from '../MineModel/Meshes/geometryLoader';
import {
  buildAutoPourByPreset,
  buildAutoRecipeFormResult,
  findRecipeForPreset,
  getPresetSequence,
  getRecipeWarningCodes,
  plusCuringDays,
  positiveNumber,
  PlannedDate,
  resolvePresetPourPlan,
  samePlannedDate,
  stopeStartPourDate,
  zonedPourDateToLocal
} from './autoPourRecipe';

type AddAllOption = { addAllStopes: true };
type StopeOption = PCStope | AddAllOption;

const ADD_ALL_SENTINEL: AddAllOption = { addAllStopes: true };
const stopeFilter = createFilterOptions<StopeOption>();

const isAddAllSentinel = (option: StopeOption): option is AddAllOption => (
  (option as AddAllOption).addAllStopes === true
);

interface ApiError {
  result?: { errors?: Record<string, string[]>; title?: string };
  message?: string;
}

interface AutoPourRunResult {
  created: number;
  adjusted: number;
  skippedExisting: number;
  skippedNoPresetGroup: number;
  skippedNoPreset: number;
  skippedNoDate: number;
  skippedNoShape: number;
  skippedNoRecipe: number;
  skippedBlocked: number;
  failed: number;
  errors: string[];
  // Reported, not counted as failures: the recipe and pour are still created.
  recipeBelowTarget: string[];
  createdPourIds: string[];
}

type NumericResultKey = Exclude<keyof AutoPourRunResult, 'errors' | 'recipeBelowTarget' | 'createdPourIds'>;

// A pour resolved to either an already-existing recipe (recipeId), one of the new recipe forms queued for
// the single bulk recipe create (newRecipeIndex into the run's newRecipeForms array), or a recipe-less
// rock-fill pour (rockFill) created individually via createRockFillPour.
interface PlannedPour {
  recipeId?: string;
  newRecipeIndex?: number;
  rockFill?: { stopeId: string; presetId: string };
  pour: CreatePourItem['pour'];
}

interface AutoPourFormProps {
  modelState: ModelState;
  onComplete: () => void;
}

const emptyRunResult = (): AutoPourRunResult => ({
  created: 0,
  adjusted: 0,
  skippedExisting: 0,
  skippedNoPresetGroup: 0,
  skippedNoPreset: 0,
  skippedNoDate: 0,
  skippedNoShape: 0,
  skippedNoRecipe: 0,
  skippedBlocked: 0,
  failed: 0,
  errors: [],
  recipeBelowTarget: [],
  createdPourIds: [],
});

const addCreatedPourIds = (result: AutoPourRunResult, ids: string[]): AutoPourRunResult => (
  ids.length === 0 ? result : { ...result, createdPourIds: [...result.createdPourIds, ...ids] }
);

const addResultCount = (
  result: AutoPourRunResult,
  key: NumericResultKey,
  amount = 1,
): AutoPourRunResult => ({
  ...result,
  [key]: (result[key] as number) + amount,
});

const addResultError = (result: AutoPourRunResult, message: string): AutoPourRunResult => ({
  ...addResultCount(result, 'failed'),
  errors: [...result.errors, message],
});

const addRecipeBelowTarget = (result: AutoPourRunResult, stopeName: string): AutoPourRunResult => (
  result.recipeBelowTarget.includes(stopeName)
    ? result
    : { ...result, recipeBelowTarget: [...result.recipeBelowTarget, stopeName] }
);

const extractApiErrorMessage = (err: unknown, fallback: string): string => {
  const e = err as ApiError;
  return e?.result?.errors?.['']?.[0] || e?.result?.title || e?.message || fallback;
};

const getAllStopes = (model: Model): PCStope[] => {
  const modelWithAllStopes = model as Model & { allStopes?: Record<string, PCStope[]> };
  const stopes = Object.values(modelWithAllStopes.allStopes ?? model.stopes ?? {})
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((stope): stope is PCStope => !!stope?.data?.stopeId);
  const byId = new Map<string, PCStope>();
  stopes.forEach((stope) => byId.set(stope.data.stopeId, stope));
  return Array.from(byId.values())
    .sort((a, b) => (a.data.stopeName ?? a.data.stopeId).localeCompare(b.data.stopeName ?? b.data.stopeId));
};

// The stope's total fillable volume - the enclosed volume of the solid, which is what the fill view's
// totals are measured against, so the "fill remaining" volume equals the true remainder.
const computeStopeFillVolume = (mesh: THREE.Mesh): number | undefined => positiveNumber(meshVolume(mesh));

const disposeMesh = (mesh: THREE.Mesh | null) => {
  if (!mesh) return;
  mesh.geometry.dispose();
  const material = mesh.material as THREE.Material | THREE.Material[];
  if (Array.isArray(material)) {
    material.forEach((m) => m.dispose());
  } else {
    material.dispose();
  }
};

// Use the SAME loader/processing as the stope-fill view (useStopeModel: rotate, clean, centre,
// move-to-min). Loading the raw geometry instead produced an unclean, differently-framed mesh whose
// CSG pour volumes were wildly wrong (a 5m plug measured larger than the whole stope).
const loadStopeMesh = async (stope: PCStope): Promise<THREE.Mesh | null> => {
  const path = stope.data.stopeGeometryFile?.path;
  if (!path) return null;

  const result = await useStopeModel(buildMediaUrl(path));
  return (result?.stopeModel as unknown as THREE.Mesh) ?? null;
};

const normalizeRecipeIds = (result: string | string[] | undefined): string[] => {
  if (Array.isArray(result)) return result.filter(Boolean);
  return result ? [result] : [];
};

const buildResultMessage = (result: AutoPourRunResult): string => {
  const skipped = result.skippedNoPresetGroup
    + result.skippedNoPreset
    + result.skippedNoDate
    + result.skippedNoShape
    + result.skippedNoRecipe
    + result.skippedBlocked;

  return i18n._(
    '{created} pours created. {adjusted} dates adjusted. {existing} existing. {skipped} skipped. {failed} failed.',
    {
      created: result.created,
      adjusted: result.adjusted,
      existing: result.skippedExisting,
      skipped,
      failed: result.failed,
    },
  );
};

const buildSkipDetails = (result: AutoPourRunResult): string => ([
  result.skippedNoPresetGroup ? i18n._('{count} without preset group', { count: result.skippedNoPresetGroup }) : null,
  result.skippedNoPreset ? i18n._('{count} without presets', { count: result.skippedNoPreset }) : null,
  result.skippedNoDate ? i18n._('{count} without start date', { count: result.skippedNoDate }) : null,
  result.skippedNoShape ? i18n._('{count} without usable stope shape or volume', { count: result.skippedNoShape }) : null,
  result.skippedNoRecipe ? i18n._('{count} without recipe inputs', { count: result.skippedNoRecipe }) : null,
  result.skippedBlocked ? i18n._('{count} blocked by earlier skipped pours', { count: result.skippedBlocked }) : null,
]).filter(Boolean).join(', ');

const AutoPourForm = ({ modelState, onComplete }: AutoPourFormProps) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { activeScenarioId } = useScenario();
  const { actions: { setUndoActionOverride, clearUndoActionOverride } } = usePage();
  const createRecipeMutation = useCreateRecipeMutation();
  const projectId = modelState.model.project.entityId;
  // Auto pours have no per-pour override (D5): the whole batch takes the project default. The pours
  // themselves are left without a stored angle so the backend stamps the same default once per batch;
  // this copy only drives the client-side height -> volume estimate shown in the run summary.
  const projectDefaultBeachAngle = modelState.model.project?.defaultPasteBeachAngleDegrees
    ?? LEGACY_BEACH_ANGLE_DEGREES;
  const [selectedStopes, setSelectedStopes] = React.useState<PCStope[]>([]);
  const [selectedPourPresetGroup, setSelectedPourPresetGroup] = React.useState<PourPresetGroupDto | null>(null);
  const [inputValue, setInputValue] = React.useState('');
  const [createMissingPours, setCreateMissingPours] = React.useState(true);
  const [adjustExistingSchedule, setAdjustExistingSchedule] = React.useState(true);
  const [isWorking, setIsWorking] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<AutoPourRunResult | null>(null);
  const filteredStopesRef = React.useRef<PCStope[]>([]);

  const { data: allProjectRecipes } = useAllProjectRecipesQuery({ projectId, scenarioId: activeScenarioId ?? undefined });
  const { data: pourPresetGroups, isLoading: pourPresetGroupsLoading } = useGetPourPresetGroupsQuery({
    projectId,
    page: 1,
    pageSize: 1000,
  });

  const stopeOptions = React.useMemo(() => getAllStopes(modelState.model), [modelState.model]);
  const pourPresetGroupOptions = React.useMemo(() => (
    [...(pourPresetGroups?.items ?? [])]
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
  ), [pourPresetGroups?.items]);
  const groupsById = React.useMemo(() => new Map(
    (pourPresetGroups?.items ?? []).map((group) => [group.entityId, group]),
  ), [pourPresetGroups?.items]);

  React.useEffect(() => {
    if (selectedStopes.length > 0 || !modelState.model.selectedStope?.data?.stopeId) return;
    setSelectedStopes([modelState.model.selectedStope]);
  }, [modelState.model.selectedStope, selectedStopes.length]);

  const filterOptions = React.useCallback(
    (options: StopeOption[], state: FilterOptionsState<StopeOption>): StopeOption[] => {
      const filtered = stopeFilter(options, state) as PCStope[];
      filteredStopesRef.current = filtered;
      const selectedIds = new Set(selectedStopes.map((stope) => stope.data.stopeId));
      const unselectedCount = filtered.filter((stope) => !selectedIds.has(stope.data.stopeId)).length;
      return unselectedCount > 0 ? [ADD_ALL_SENTINEL, ...filtered] : filtered;
    },
    [selectedStopes],
  );

  // Resolves the recipe for a stope+preset without creating it. Existing recipes return their id; missing
  // ones are queued onto the run-wide newRecipeForms array (deduped by stope+preset) so the whole run can
  // create them in ONE bulk call. Returns the warning codes the pour should inherit.
  const planRecipe = (
    stope: PCStope,
    preset: PourPresetDto,
    recipes: BackfillRecipeDto[],
    newRecipeForms: BackfillRecipeForm[],
    newRecipeStopeNames: string[],
    newRecipeIndexByKey: Map<string, number>,
  ): { recipeId?: string; newRecipeIndex?: number; acceptedWarnings: string[]; optimisationFailed?: boolean } | null => {
    const key = `${stope.data.stopeId}:${preset.entityId}`;
    const queuedIndex = newRecipeIndexByKey.get(key);
    if (queuedIndex !== undefined) {
      const queuedForm = newRecipeForms[queuedIndex];
      return {
        newRecipeIndex: queuedIndex,
        acceptedWarnings: (queuedForm.warnings?.map((warning) => warning.code).filter(Boolean) as string[]) ?? [],
      };
    }

    const recipe = findRecipeForPreset(recipes, stope.data.stopeId, preset.entityId);
    if (recipe?.entityId) {
      return {
        recipeId: recipe.entityId,
        acceptedWarnings: getRecipeWarningCodes(recipe),
      };
    }

    const built = buildAutoRecipeFormResult(modelState.model, stope, preset);
    if (!built) return null;

    const { form: recipeForm, optimisationFailed } = built;
    const newRecipeIndex = newRecipeForms.length;
    newRecipeForms.push(recipeForm);
    newRecipeStopeNames.push(stope.data.stopeName);
    newRecipeIndexByKey.set(key, newRecipeIndex);
    return {
      newRecipeIndex,
      acceptedWarnings: (recipeForm.warnings?.map((warning) => warning.code).filter(Boolean) as string[]) ?? [],
      optimisationFailed,
    };
  };

  const planStope = async (
    stope: PCStope,
    currentResult: AutoPourRunResult,
    recipes: BackfillRecipeDto[],
    newRecipeForms: BackfillRecipeForm[],
    newRecipeStopeNames: string[],
    newRecipeIndexByKey: Map<string, number>,
  ): Promise<{ result: AutoPourRunResult; plannedPours: PlannedPour[] }> => {
    const presets = getPresetSequence(stope, groupsById, selectedPourPresetGroup);
    if (!presets) {
      return { result: addResultCount(currentResult, 'skippedNoPresetGroup'), plannedPours: [] };
    }
    if (presets.length === 0) {
      return { result: addResultCount(currentResult, 'skippedNoPreset'), plannedPours: [] };
    }

    // includeRockFill so rock-fill pours already created (e.g. via the stope-fill view) are matched to their
    // preset and counted as existing, rather than treated as missing and duplicated below.
    const existingPours = await api.pours.getAll(projectId, stope.data.stopeId, activeScenarioId ?? undefined, true);
    const autoPoursByPreset = buildAutoPourByPreset(existingPours, recipes);
    const firstExistingDate = presets
      .map((preset) => autoPoursByPreset.get(preset.entityId)?.pour)
      .map((pour) => zonedPourDateToLocal(pour?.intendedPourDate))
      .find((date): date is PlannedDate => !!date);

    const plannedDate = stopeStartPourDate(stope) ?? firstExistingDate;
    if (!plannedDate) {
      return { result: addResultCount(currentResult, 'skippedNoDate', presets.length), plannedPours: [] };
    }

    const needsMesh = presets.some((preset) => (
      !!positiveNumber(preset.height)
      || (preset.fillRemainingHeight && !positiveNumber(stope.data.volume))
    ));
    const mesh = needsMesh ? await loadStopeMesh(stope) : null;
    // Prefer the mesh-derived total (consistent units with the per-pour CSG volumes and the fill view);
    // only fall back to the recorded stope volume when no mesh is available.
    const remainingVolume = (mesh ? computeStopeFillVolume(mesh) : undefined) ?? positiveNumber(stope.data.volume);
    const plannedPours: PlannedPour[] = [];

    try {
      const finalState = await presets.reduce<Promise<{
        result: AutoPourRunResult;
        plannedDate: PlannedDate;
        remainingVolume: number | undefined;
        sequenceBlocked: boolean;
      }>>(async (statePromise, preset) => {
        const state = await statePromise;
        const existing = autoPoursByPreset.get(preset.entityId);
        const nextPlannedDate = plusCuringDays(state.plannedDate, preset.curingDays);

        if (existing) {
          let nextResult = state.result;
          if (adjustExistingSchedule && existing.pour.entityId && !samePlannedDate(zonedPourDateToLocal(existing.pour.intendedPourDate), state.plannedDate)) {
            await api.pours.updatePourDate(existing.pour.entityId, { pourDate: state.plannedDate });
            nextResult = addResultCount(nextResult, 'adjusted');
          }

          nextResult = addResultCount(nextResult, 'skippedExisting');
          const existingVolume = positiveNumber(existing.pour.volume);
          const nextRemainingVolume = existingVolume && state.remainingVolume !== undefined
            ? Math.max(0, state.remainingVolume - existingVolume)
            : state.remainingVolume;
          return {
            ...state,
            result: nextResult,
            plannedDate: nextPlannedDate,
            remainingVolume: nextRemainingVolume,
          };
        }

        if (!createMissingPours) {
          return {
            ...state,
            plannedDate: nextPlannedDate,
          };
        }

        if (state.sequenceBlocked) {
          return {
            ...state,
            result: addResultCount(state.result, 'skippedBlocked'),
            plannedDate: nextPlannedDate,
          };
        }

        const resolution = resolvePresetPourPlan(preset, state.plannedDate, mesh, state.remainingVolume, projectDefaultBeachAngle);
        if (!resolution.plan) {
          return {
            ...state,
            result: resolution.skip ? addResultCount(state.result, resolution.skip) : state.result,
            plannedDate: nextPlannedDate,
            sequenceBlocked: true,
          };
        }

        const basePour = {
          reference: i18n._('Auto - {stopeName} - {presetName}', {
            stopeName: stope.data.stopeName,
            presetName: preset.name,
          }),
          plannedDate: resolution.plan.plannedDate,
          volume: resolution.plan.volume,
          pourPositionX: resolution.plan.pourPositionX,
          pourPositionY: resolution.plan.pourPositionY,
          pourHeight: resolution.plan.pourHeight,
        };

        const recipePlan = preset.isRockFill
          ? null
          : planRecipe(stope, preset, recipes, newRecipeForms, newRecipeStopeNames, newRecipeIndexByKey);

        if (preset.isRockFill) {
          // Rock-fill presets have no recipe — queue a recipe-less pour created directly against the stope,
          // mirroring the stope-fill view's createRockFillPour path.
          plannedPours.push({
            rockFill: { stopeId: stope.data.stopeId, presetId: preset.entityId },
            pour: { ...basePour, acceptedWarnings: [] },
          });
        } else if (!recipePlan) {
          return {
            ...state,
            result: addResultCount(state.result, 'skippedNoRecipe'),
            plannedDate: nextPlannedDate,
            sequenceBlocked: true,
          };
        } else {
          plannedPours.push({
            recipeId: recipePlan.recipeId,
            newRecipeIndex: recipePlan.newRecipeIndex,
            pour: { ...basePour, acceptedWarnings: recipePlan.acceptedWarnings },
          });
        }

        const nextRemainingVolume = state.remainingVolume !== undefined
          ? Math.max(0, state.remainingVolume - resolution.plan.volume)
          : state.remainingVolume;
        return {
          result: recipePlan?.optimisationFailed
            ? addRecipeBelowTarget(state.result, stope.data.stopeName ?? stope.data.stopeId)
            : state.result,
          plannedDate: nextPlannedDate,
          remainingVolume: nextRemainingVolume,
          sequenceBlocked: false,
        };
      }, Promise.resolve({
        result: currentResult,
        plannedDate,
        remainingVolume,
        sequenceBlocked: false,
      }));

      return { result: finalState.result, plannedPours };
    } finally {
      disposeMesh(mesh);
    }
  };

  // Registers the global toolbar Undo so it bulk-deletes exactly the pours created in this run.
  // Uses the api directly (not a mutation hook) and the app-level queryClient so the handler keeps
  // working after this dialog has been closed/unmounted.
  const publishAutoPourUndo = React.useCallback((pourIds: string[]) => {
    if (pourIds.length === 0) return;

    const handleUndo = () => {
      setUndoActionOverride((current) => (current ? { ...current, enabled: false, loading: true } : current));

      api.pours.deletePours({ pourIds })
        .then(async () => {
          clearUndoActionOverride();
          await queryClient.invalidateQueries('pours', { exact: false });
          await queryClient.invalidateQueries('paged_pours', { exact: false });
          await queryClient.invalidateQueries('stopeBackfills', { exact: false });
          await queryClient.invalidateQueries('stopeBackfill', { exact: false });
        })
        .catch(() => {
          setUndoActionOverride({ enabled: true, loading: false, onClick: handleUndo });
        });
    };

    setUndoActionOverride({ enabled: true, loading: false, onClick: handleUndo });
  }, [api, clearUndoActionOverride, queryClient, setUndoActionOverride]);

  const handleRun = async () => {
    if (selectedStopes.length === 0) {
      setError(i18n._('Select at least one stope.'));
      return;
    }
    if (!createMissingPours && !adjustExistingSchedule) {
      setError(i18n._('Select at least one auto pour action.'));
      return;
    }

    setIsWorking(true);
    setError(null);
    setResult(null);
    try {
      const recipes = allProjectRecipes ?? [];
      const newRecipeForms: BackfillRecipeForm[] = [];
      const newRecipeStopeNames: string[] = [];
      const newRecipeIndexByKey = new Map<string, number>();

      // Pass 1: plan every stope. Existing pours/dates are read here and new recipe forms are queued, but
      // nothing is written yet, so the whole run can create recipes and pours in one bulk call each.
      const planned = await selectedStopes.reduce<Promise<{ result: AutoPourRunResult; plannedPours: PlannedPour[] }>>(
        async (statePromise, stope) => {
          const state = await statePromise;
          try {
            const stopePlan = await planStope(
              stope, state.result, recipes, newRecipeForms, newRecipeStopeNames, newRecipeIndexByKey,
            );
            return {
              result: stopePlan.result,
              plannedPours: [...state.plannedPours, ...stopePlan.plannedPours],
            };
          } catch (err) {
            return {
              result: addResultError(
                state.result,
                `${stope.data.stopeName}: ${extractApiErrorMessage(err, 'Failed to create auto pours')}`,
              ),
              plannedPours: state.plannedPours,
            };
          }
        },
        Promise.resolve({ result: emptyRunResult(), plannedPours: [] }),
      );

      let runResult = planned.result;

      // Pass 2: create all queued recipes in a single bulk call (one notification, one round-trip). The
      // backend returns ids in input order, so newRecipeIndex maps straight onto createdRecipeIds.
      let createdRecipeIds: string[] = [];
      if (newRecipeForms.length > 0) {
        const createdRaw = await createRecipeMutation.mutateAsync({
          projectId,
          data: newRecipeForms,
          context: { stopeName: Array.from(new Set(newRecipeStopeNames)) },
        });
        createdRecipeIds = normalizeRecipeIds(createdRaw as unknown as string | string[] | undefined);
      }

      // Pass 3: resolve each recipe-backed pour to a concrete recipe id, then create the pours in one bulk
      // call. Rock-fill pours have no recipe and are created separately below.
      const poursToCreate: CreatePourItem[] = [];
      planned.plannedPours.forEach((plannedPour) => {
        if (plannedPour.rockFill) return;
        const recipeId = plannedPour.recipeId
          ?? (plannedPour.newRecipeIndex !== undefined ? createdRecipeIds[plannedPour.newRecipeIndex] : undefined);
        if (!recipeId) {
          runResult = addResultError(
            runResult,
            i18n._('{reference}: recipe could not be created', { reference: plannedPour.pour?.reference ?? i18n._('Pour') }),
          );
          return;
        }
        poursToCreate.push({ recipeId, pour: plannedPour.pour });
      });

      if (poursToCreate.length > 0) {
        const bulkResult = await api.pours.createPours({ pours: poursToCreate, isAutoPour: true });
        const createdCount = bulkResult?.createdPourIds?.length ?? poursToCreate.length;
        runResult = addCreatedPourIds(
          addResultCount(runResult, 'created', createdCount),
          bulkResult?.createdPourIds ?? [],
        );
        runResult = (bulkResult?.failures ?? []).reduce(
          (acc, failure) => addResultError(
            acc,
            `${failure.reference || i18n._('Pour')}: ${failure.error || i18n._('Failed to create pour')}`,
          ),
          runResult,
        );
      }

      // Pass 4: create the recipe-less rock-fill pours. There is no bulk endpoint, so each is created
      // individually against its stope and linked to its preset.
      const rockFillPours = planned.plannedPours.filter((plannedPour) => !!plannedPour.rockFill);
      runResult = await rockFillPours.reduce(async (accPromise, plannedPour) => {
        const acc = await accPromise;
        const { stopeId, presetId } = plannedPour.rockFill!;
        try {
          const pourId = await api.pours.createRockFillPour(
            projectId, stopeId, plannedPour.pour!, activeScenarioId ?? undefined, presetId,
          );
          return addCreatedPourIds(addResultCount(acc, 'created'), pourId ? [pourId] : []);
        } catch (err) {
          return addResultError(
            acc,
            `${plannedPour.pour?.reference ?? i18n._('Pour')}: ${extractApiErrorMessage(err, 'Failed to create rock fill pour')}`,
          );
        }
      }, Promise.resolve(runResult));

      await queryClient.invalidateQueries('pours', { exact: false });
      await queryClient.invalidateQueries('paged_pours', { exact: false });
      await queryClient.invalidateQueries('stopeBackfills', { exact: false });
      await queryClient.invalidateQueries('stopeBackfill', { exact: false });

      setResult(runResult);
      publishAutoPourUndo(runResult.createdPourIds);
    } finally {
      setIsWorking(false);
    }
  };

  const hasRheology = modelState.model.rheology && modelState.model.rheology.length > 0;
  const disableRun = isWorking || pourPresetGroupsLoading;

  return (
    <Dialog open onClose={isWorking ? undefined : onComplete} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n._('Auto Pour Creation')}</DialogTitle>
      <DialogContent>
        {!hasRheology ? (
          <Alert severity="error">
            {i18n._('No rheology data has been added to this project. Please add rheology data before creating auto pours.')}
          </Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete<StopeOption, true, false, false>
              multiple
              id="auto-pour-stope-selector"
              options={stopeOptions}
              value={selectedStopes}
              inputValue={inputValue}
              onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
              filterOptions={filterOptions}
              getOptionLabel={(option) => {
                if (isAddAllSentinel(option)) {
                  return inputValue ? i18n._('Add Matching Stopes') : i18n._('Add All Stopes');
                }
                return option.data?.stopeName ?? option.data?.stopeId ?? '';
              }}
              isOptionEqualToValue={(opt, val) => {
                if (isAddAllSentinel(opt) || isAddAllSentinel(val)) return false;
                return opt.data?.stopeId === val.data?.stopeId;
              }}
              onChange={(_event, newValue) => {
                if (newValue.some(isAddAllSentinel)) {
                  const selectedIds = new Set(selectedStopes.map((stope) => stope.data.stopeId));
                  const unselected = filteredStopesRef.current.filter((stope) => !selectedIds.has(stope.data.stopeId));
                  setSelectedStopes([...selectedStopes, ...unselected]);
                } else {
                  setSelectedStopes(newValue as PCStope[]);
                }
              }}
              disableCloseOnSelect
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                if (isAddAllSentinel(option)) {
                  return (
                    <li key="add-all-stopes" {...optionProps}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                        {inputValue ? i18n._('Add Matching Stopes') : i18n._('Add All Stopes')}
                      </Typography>
                    </li>
                  );
                }
                return (
                  <li key={key} {...optionProps}>
                    {option.data?.stopeName ?? option.data?.stopeId ?? ''}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n._('Select Stopes')}
                  placeholder={i18n._('Select Stopes')}
                />
              )}
            />

            <Autocomplete<PourPresetGroupDto, false, false, false>
              id="auto-pour-preset-group-selector"
              options={pourPresetGroupOptions}
              value={selectedPourPresetGroup}
              loading={pourPresetGroupsLoading}
              onChange={(_event, group) => setSelectedPourPresetGroup(group)}
              getOptionLabel={(group) => group.name ?? ''}
              isOptionEqualToValue={(option, value) => option.entityId === value.entityId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n._('Pour Preset Group')}
                  placeholder={i18n._('Use stope preset group')}
                />
              )}
            />

            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={createMissingPours}
                    onChange={(event) => setCreateMissingPours(event.target.checked)}
                  />
                )}
                label={i18n._('Create missing pours')}
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={adjustExistingSchedule}
                    onChange={(event) => setAdjustExistingSchedule(event.target.checked)}
                  />
                )}
                label={i18n._('Adjust auto pour dates')}
              />
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}

            {result && (
              <Alert severity={result.failed > 0 || result.recipeBelowTarget.length > 0 ? 'warning' : 'success'}>
                <Typography variant="body2">{buildResultMessage(result)}</Typography>
                {buildSkipDetails(result) && (
                  <Typography variant="caption">{buildSkipDetails(result)}</Typography>
                )}
                {result.recipeBelowTarget.length > 0 && (
                  <Typography variant="caption" component="div">
                    {i18n._('Recipe target UCS not reached for: {stopes}', { stopes: result.recipeBelowTarget.join(', ') })}
                  </Typography>
                )}
              </Alert>
            )}

            {result?.errors.slice(0, 4).map((message) => (
              <Alert key={message} severity="error">{message}</Alert>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onComplete} disabled={isWorking}>
          {i18n._('Close')}
        </Button>
        {hasRheology && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleRun}
            disabled={disableRun}
            startIcon={isWorking ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {i18n._('Run Auto Pour')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AutoPourForm;
